import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleLeft, faAngleRight } from '@fortawesome/pro-regular-svg-icons';
import { cn } from '@v2/shared/lib/utils';

type Props = {
  currentPage: number;
  totalPages: number;
  onSelect: (page: number) => void;
  isLoading: boolean;
};

/** How many numbered buttons to show before the gap. */
const PAGE_LINKS = 4;

const BUTTON_CLASS = cn(
  'flex h-9 min-w-[38px] cursor-pointer items-center justify-center rounded-lg border border-solid px-2',
  'text-sm disabled:cursor-not-allowed disabled:opacity-50'
);

/**
 * The two states are kept apart rather than layered, because `cn` is clsx and does not
 * resolve conflicting Tailwind utilities — `bg-primary` after `bg-white` in the class
 * list loses to whichever the stylesheet happens to define last.
 */
const RESTING_CLASS = 'border-slate-200 bg-white text-slate-600 hover:border-primary hover:text-primary';

const CURRENT_CLASS = 'border-primary bg-primary font-bold text-white';

/**
 * Buttons rather than links, because turning a page is an API call now: page 4 is a
 * request for the next fifteen reviews, not a url. Deliberately so — thousands of thin
 * paginated addresses hanging off a page that ranks top 3 is a cost with no upside, and
 * the reviews worth ranking for are the ones the server already rendered.
 *
 * Everything is disabled while a page is in flight, so an impatient double click cannot
 * queue up two requests and land on whichever answers last.
 */
export default function Pager({ currentPage, totalPages, onSelect, isLoading }: Props) {
  if (totalPages <= 1) {
    return null;
  }

  const numbers: number[] = [];

  for (let page = 1; page <= Math.min(PAGE_LINKS, totalPages); page++) {
    numbers.push(page);
  }

  if (currentPage > PAGE_LINKS && currentPage < totalPages) {
    numbers.push(currentPage);
  }

  if (totalPages > PAGE_LINKS) {
    numbers.push(totalPages);
  }

  const pages = Array.from(new Set(numbers)).sort((a, b) => a - b);

  return (
    <nav aria-label="Reviews pages" className="mt-7 flex items-center justify-center gap-1.5">
      <button
        type="button"
        aria-label="Previous page"
        disabled={isLoading || currentPage <= 1}
        onClick={() => onSelect(currentPage - 1)}
        className={cn(BUTTON_CLASS, RESTING_CLASS)}
      >
        <FontAwesomeIcon icon={faAngleLeft} aria-hidden />
      </button>

      {pages.map((page, index) => (
        <span key={page} className="flex items-center gap-1.5">
          {index > 0 && page > pages[index - 1] + 1 && (
            <span className="px-1 text-slate-400" aria-hidden>
              …
            </span>
          )}

          <button
            type="button"
            aria-current={page === currentPage ? 'page' : undefined}
            aria-label={`Page ${page}`}
            disabled={isLoading}
            onClick={() => onSelect(page)}
            className={cn(BUTTON_CLASS, page === currentPage ? CURRENT_CLASS : RESTING_CLASS)}
          >
            {page}
          </button>
        </span>
      ))}

      <button
        type="button"
        aria-label="Next page"
        disabled={isLoading || currentPage >= totalPages}
        onClick={() => onSelect(currentPage + 1)}
        className={cn(BUTTON_CLASS, RESTING_CLASS)}
      >
        <FontAwesomeIcon icon={faAngleRight} aria-hidden />
      </button>
    </nav>
  );
}
